import { useEffect, useRef, useState } from "react";

type VideoPlayerProps = {
	url: string;
	captionsUrl?: string;
	poster?: string;
};

export default function VideoPlayer({
	url,
	captionsUrl,
	poster
}: VideoPlayerProps) {
	const divRef = useRef<HTMLDivElement>(null);
	const [shouldLoad, setShouldLoad] = useState(false);

	useEffect(() => {
		if (!divRef.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setShouldLoad(true);
						observer.disconnect();
					}
				});
			},
			{ threshold: 0.25 }
		);

		observer.observe(divRef.current);

		// eslint-disable-next-line consistent-return
		return () => observer.disconnect();
	}, []);

	if (!url) return null;

	const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
	const isInstagram = url.includes("instagram.com");
	const isMP4 = url.endsWith(".mp4");

	if (isYouTube) {
		const videoId = url.split("v=")[1]?.split("&")[0] || url.split("/").pop();
		return (
			<iframe
				className="w-full aspect-video rounded-2xl"
				src={`https://www.youtube-nocookie.com/embed/${videoId}`}
				title="YouTube video player"
				frameBorder="0"
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
				referrerPolicy="strict-origin-when-cross-origin"
				allowFullScreen
			/>
		);
	}

	if (isInstagram) {
		return (
			<iframe
				className="w-full aspect-square rounded-2xl"
				src={`${url}embed`}
				title="Instagram video"
				frameBorder="0"
				allowFullScreen
			/>
		);
	}

	if (isMP4) {
		return (
			<div ref={divRef}>
				{shouldLoad ? (
					<video
						className="w-full rounded-2xl"
						controls
						poster={poster}
						preload="metadata"
						playsInline
						muted
					>
						<source src={url} type="video/mp4" />
						{captionsUrl && (
							<track
								src={captionsUrl}
								kind="captions"
								srcLang="pt"
								label="Português"
								default
							/>
						)}
					</video>
				) : (
					poster && (
						<img
							src={poster}
							alt="Pré-visualização do vídeo"
							className="w-full rounded-2xl"
						/>
					)
				)}
			</div>
		);
	}

	return <p className="text-red-500">Formato de vídeo não suportado: {url}</p>;
}
